import {
	CheckCircleFilled,
	CloseCircleFilled,
	EyeOutlined,
	FormOutlined,
	LoadingOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Col, Row, Space, Spin, Tag } from 'antd';
import Text from 'antd/lib/typography/Text';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CustomNotification } from 'src/components/custom-notification';
import { tableColumnTextFilterConfig } from 'src/components/tableUtils';
import WeightChange from 'src/components/ui/weight-change';

import {
	MutationDeleteThingsToDo,
	MutationUpdateThingsToDo,
} from 'src/lib/gql/mutations';
import { QueryThingsToDo } from 'src/lib/gql/queries';
import { ThingsToDoTable } from 'src/lib/interface';
import InfoTable from '../components/info-table';
import { ROUTES } from '../config/route';
import {
	CmsDeleteThingsToDoMutation,
	CmsDeleteThingsToDoMutationVariables,
	CmsGetThingsToDoQuery,
	CmsUpdateThingsToDoMutation,
	CmsUpdateThingsToDoMutationVariables,
} from 'src/lib/generated/_generated';

const ThingsToDo = () => {
	const {
		data: qryData,
		loading,
		refetch: refetchQuery,
	} = useQuery<CmsGetThingsToDoQuery>(QueryThingsToDo, {
		fetchPolicy: 'cache-and-network',
	});

	//#region Update Things To Do Mutation

	const [UpdateThingsToDo] = useMutation<
		CmsUpdateThingsToDoMutation,
		CmsUpdateThingsToDoMutationVariables
	>(MutationUpdateThingsToDo, {
		onError(error) {
			CustomNotification({
				pageName: 'Things to Do',
				pagePrefix: 'Event Type',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const updateThings = useCallback(
		async (
			id: string,
			enabled: boolean | undefined,
			pos: number | null | undefined,
		) => {
			const rsp = await UpdateThingsToDo({
				variables: {
					id: id,
					enabled: enabled,
					weight: pos,
				},
			});

			if (rsp.data) {
				CustomNotification({
					pageName: 'Things to Do',
					pagePrefix: 'Event type',
					notificationType: 'updated',
				});

				refetchQuery();
			}
		},
		[qryData?.getEventTypes],
	);

	//#endregion

	//#region Delete Things To Do Mutation

	const [DeleteThingsToDo] = useMutation<
		CmsDeleteThingsToDoMutation,
		CmsDeleteThingsToDoMutationVariables
	>(MutationDeleteThingsToDo, {
		onError(error) {
			CustomNotification({
				pageName: 'Things to Do',
				pagePrefix: 'Event Type',
				notificationType: 'custom-error',
				customDescription: error.message,
			});
		},
	});

	const deleteThings = async (id: string) => {
		if (!confirm('Confirm delete?')) return;
		const rsp = await DeleteThingsToDo({
			variables: {
				id: id,
			},
		});

		if (rsp.data) {
			CustomNotification({
				pageName: 'Things to Do',
				pagePrefix: 'Event type',
				notificationType: 'deleted',
			});
			refetchQuery();
		}
	};

	//#endregion

	//#region THINGS TO DO LIST

	const p_ThingsToDo: ThingsToDoTable[] = [];

	const p_ThingsToDo_Query = qryData?.getEventTypes?.forEach((thing, i) => {
		return p_ThingsToDo.push({
			id: thing?.id || '',
			key: (i + 1).toString() || '',
			name: thing?.name || '',
			tag: thing?.hTag || '',
			enabled: thing?.enabled || false,
			weight: thing?.weight || 0,
		});
	});

	// p_ThingsToDo.sort((a, b) => a.weight - b.weight);

	//#endregion

	//#region Column Data
	const columns = [
		{
			title: 'ID',
			dataIndex: 'key',
			width: 50,
			align: 'center',
			render: (key: number) => {
				return key;
			},
		},
		{
			title: 'Position',
			width: 80,
			sorter: (a, b) => a.weight - b.weight,
			render: ({ id, weight }: { id: string; weight: number }) => {
				return (
					<WeightChange
						pos={weight}
						onSubmitChange={(weight) => {
							updateThings(id, undefined, weight);
						}}
					/>
				);
			},
		},
		{
			title: 'Title',
			dataIndex: 'name',
			key: 'title',
			width: 200,
			...tableColumnTextFilterConfig<ThingsToDoTable>(),
			onFilter: (value, record) => {
				return record.name
					.toString()
					.toLowerCase()
					.includes(value.toString().toLowerCase());
			},
		},
		{
			title: 'Tag',
			key: 'tag',
			dataIndex: 'tag',
			width: 200,
			align: 'center',
			render: (tag: string) => (
				<Tag
					color={'geekblue'}
					key={tag}
					style={{
						margin: '2px',
						display: 'block',
					}}
				>
					{tag.toUpperCase()}
				</Tag>
			),
		},

		{
			title: 'Edit',
			key: 'enabled',
			width: 80,
			align: 'center',
			filters: [
				{
					text: 'Enabled',
					value: true,
				},
				{
					text: 'Disabled',
					value: false,
				},
			],
			filterMultiple: false,
			onFilter: (value, record) => record.enabled === value,
			render: (props: { enabled: boolean; id: string; key: string }) => {
				const { enabled, id } = props;

				return (
					<Space>
						<a href={``} target="_blank" rel="noreferrer">
							<EyeOutlined
								style={{
									color: 'black',
									width: '12px',
									height: '12px',
								}}
							/>
						</a>
						<Link to={`${ROUTES.EDIT_THINGS_TO_DO.path}/${id}`}>
							<FormOutlined
								style={{
									color: 'black',
									width: '12px',
									height: '12px',
								}}
							/>
						</Link>
						<CheckCircleFilled
							onClick={() => {
								updateThings(id, !enabled, undefined);
							}}
							style={{
								color: enabled ? 'var(--valid)' : 'var(--inactive)',
								width: '12px',
								height: '12px',
							}}
						/>
						<CloseCircleFilled
							onClick={() => {
								deleteThings(id);
							}}
							style={{
								color: 'var(--invalid)',
								width: '12px',
								height: '12px',
							}}
						/>
					</Space>
				);
			},
		},
	];

	//#endregion

	return (
		<Row>
			<Col span={24}>
				<Text className="sub-header">Things to Do</Text>
			</Col>

			<Col span={24}>
				<Row
					gutter={16}
					style={{
						margin: '16px 0',
					}}
					align={'bottom'}
				>
					<Link to={ROUTES.CREATE_THINGS_TO_DO.path}>
						<Button className="btn-main">
							Create New Category
							<PlusOutlined
								style={{
									position: 'relative',
									top: '1.5px',
								}}
							/>
						</Button>
					</Link>
				</Row>

				<Spin indicator={<LoadingOutlined />} spinning={loading ? true : false}>
					<InfoTable<ThingsToDoTable>
						data={p_ThingsToDo}
						columnData={columns}
						onChange={() => {
							//
						}}
						other={{
							scroll: {
								x: 900,
							},
						}}
					/>
				</Spin>
			</Col>
		</Row>
	);
};

export default ThingsToDo;
